import api from './api';

export type EstimateType = 'sale' | 'rental';
export type EstimateStatus = 'draft' | 'sent' | 'accepted' | 'converted' | 'expired';

export interface Address {
  street: string; city: string; province: string; postalCode: string; country?: string;
}

export interface EstimateLine {
  id?: string;
  position: number;
  activity: string;
  shopifyProductId?: string | null;
  shopifyVariantId?: string | null;
  productSku: string;
  productName: string;
  description: string;
  quantity: number;
  rateCents: number;
  amountCents: number;
  isBilateral?: boolean;
}

export interface Estimate {
  id: string;
  estimateNumber: string;
  type: EstimateType;
  clinicId: string | null;
  patientId: string | null;
  patientFirstName: string;
  patientLastName: string;
  patientEmail: string | null;
  patientPhone: string | null;
  shipAddress: Address;
  billAddress: Address;
  shipVia: string | null;
  estimateDate: string;
  expirationDate: string;
  province: string;
  subtotalCents: number;
  taxRateBps: number;
  taxAmountCents: number;
  totalCents: number;
  status: EstimateStatus;
  convertedToDraftOrderId: string | null;
  convertedToInvoiceUrl: string | null;
  convertedAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  lines: EstimateLine[];
}

export interface CreateEstimateInput {
  type: EstimateType;
  clinicId?: string;
  patientId?: string;
  patientFirstName: string;
  patientLastName: string;
  patientEmail?: string;
  patientPhone?: string;
  shipAddress: Address;
  billAddress?: Address;
  shipVia?: string;
  estimateDate: string;
  expirationDate: string;
  lines: Array<Omit<EstimateLine, 'id' | 'amountCents' | 'position'>>;
  notes?: string;
}

export interface ConvertInput {
  shipAddress?: Address;
  surgeryDate?: string;
  wrapProductId?: string;
  wrapVariantId?: string;
  addOns?: Array<{ productId: string; variantId: string }>;
}

export const estimatesApi = {
  list: async (params: { type?: EstimateType; status?: string; clinicId?: string; patientId?: string; search?: string; skip?: number; take?: number } = {}) => {
    const r = await api.get<{ data: Estimate[]; total: number }>('/estimates', { params });
    return r.data;
  },
  get: async (id: string) => {
    const r = await api.get<Estimate>(`/estimates/${id}`);
    return r.data;
  },
  create: async (input: CreateEstimateInput) => {
    const r = await api.post<Estimate>('/estimates', input);
    return r.data;
  },
  update: async (id: string, input: Partial<CreateEstimateInput>) => {
    const r = await api.patch<Estimate>(`/estimates/${id}`, input);
    return r.data;
  },
  markSent: async (id: string) => {
    const r = await api.post<Estimate>(`/estimates/${id}/send`);
    return r.data;
  },
  markAccepted: async (id: string) => {
    const r = await api.post<Estimate>(`/estimates/${id}/accept`);
    return r.data;
  },
  convert: async (id: string, input: ConvertInput = {}) => {
    const r = await api.post<{ status: string; draftOrderId: string; invoiceUrl?: string }>(`/estimates/${id}/convert`, input);
    return r.data;
  },
  delete: async (id: string) => {
    const r = await api.delete(`/estimates/${id}`);
    return r.data;
  },
  pdfUrl: (id: string) => {
    const basename = (import.meta as any).env?.VITE_APP_BASENAME || '';
    return `${basename}/api/estimates/${id}/pdf`;
  },
};

export default estimatesApi;
